import type { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { DESIGN_ASSET_SLOTS } from "../../Domain/DesignAssetSlots.ts";
import { loadGeneratedAssetManifest } from "../Drawable/generatedAssetManifest.ts";
import { createCardImageId } from "../Drawable/useCreateAssetIds.ts";
import { DASHBOARD_CARDS_QUERY } from "../Dashboard/dashboardCardsQuery.ts";

export const MATCH_LOBBY_IMAGE_ASSETS = DESIGN_ASSET_SLOTS.filter((slot) =>
	slot.id.startsWith("lobby-"),
).map((slot) => slot.path);

// Prepare dashboard art and the first match lobby before leaving the opening screen.
export const STARTUP_PAGE_IMAGE_ASSETS = [
	"/images/assets/menu/dashboard-citadel.webp",
	"/images/assets/menu/route-match.webp",
	"/images/assets/menu/route-campaign.webp",
	"/images/assets/menu/route-analysis.webp",
	"/images/assets/menu/route-deckbuilder.webp",
	"/images/assets/menu/route-collection-shop.webp",
	"/images/assets/menu/route-manual.webp",
	"/images/assets/menu/route-feedback.webp",
	"/images/assets/menu/route-friends.webp",
	"/images/assets/menu/route-worlds.webp",
	...MATCH_LOBBY_IMAGE_ASSETS,
] as const;

export interface StartupCardPreview {
	id: string;
	name: string;
	imageUrl: string;
}

export interface StartupPreloadProgress {
	progress: number;
	status: string;
}

interface PreloadCard {
	id: string | number;
	name: string;
	image: string | null;
	imageHash?: string | null;
}

interface RunStartupPreloadOptions {
	client: ApolloClient<NormalizedCacheObject>;
	onCards?: (cards: StartupCardPreview[]) => void;
	onProgress?: (progress: StartupPreloadProgress) => void;
	loadImage?: (url: string) => Promise<void>;
	loadPageImage?: (url: string) => Promise<void>;
}

type RunMatchLobbyPreloadOptions = Pick<RunStartupPreloadOptions, "onProgress" | "loadPageImage">;

const unique = <T>(values: T[]): T[] => [...new Set(values)];

export const selectStartupCardPreviews = (
	cards: PreloadCard[],
	limit = 5,
): StartupCardPreview[] => {
	const cardsWithArt = cards.filter((card): card is PreloadCard & { image: string } =>
		Boolean(card.image),
	);
	if (cardsWithArt.length === 0 || limit <= 0) {
		return [];
	}

	const step = Math.max(1, Math.floor(cardsWithArt.length / limit));
	return cardsWithArt
		.filter((_, index) => index % step === 0)
		.slice(0, limit)
		.map((card) => ({
			id: String(card.id),
			name: card.name,
			imageUrl: createCardImageId(card),
		}));
};

const loadBrowserImage = (url: string): Promise<void> =>
	new Promise((resolve, reject) => {
		const image = new Image();
		image.onload = () => {
			void image.decode().then(resolve, reject);
		};
		image.onerror = () => reject(new Error(`Failed to preload page image: ${url}`));
		image.src = url;
	});

export const preloadWithConcurrency = async (
	urls: string[],
	load: (url: string) => Promise<unknown>,
	onSettled: () => void,
	concurrency = 6,
	failOnError = false,
): Promise<void> => {
	let nextIndex = 0;
	const failures: string[] = [];
	const workers = Array.from(
		{ length: Math.min(Math.max(1, concurrency), urls.length) },
		async () => {
			while (nextIndex < urls.length) {
				const url = urls[nextIndex];
				nextIndex += 1;
				try {
					await load(url);
				} catch {
					failures.push(url);
				} finally {
					onSettled();
				}
			}
		},
	);
	await Promise.all(workers);
	if (failOnError && failures.length > 0) {
		throw new Error(`Dashboard assets failed to load: ${failures.join(", ")}`);
	}
};

const loadWithRetry = async (url: string, load: (url: string) => Promise<void>): Promise<void> => {
	try {
		await load(url);
	} catch {
		await load(url);
	}
};

export const runMatchLobbyPreload = async ({
	onProgress = () => undefined,
	loadPageImage = loadBrowserImage,
}: RunMatchLobbyPreloadOptions = {}): Promise<void> => {
	let settled = 0;
	onProgress({ progress: 0.08, status: "Gathering game artwork" });
	await preloadWithConcurrency(
		MATCH_LOBBY_IMAGE_ASSETS,
		(url) => loadWithRetry(url, loadPageImage),
		() => {
			settled += 1;
			onProgress({
				progress: 0.08 + (settled / MATCH_LOBBY_IMAGE_ASSETS.length) * 0.9,
				status: "Gathering game artwork",
			});
		},
		3,
		true,
	);
	onProgress({ progress: 1, status: "The table is ready" });
};

export const runStartupPreload = async ({
	client,
	onCards = () => undefined,
	onProgress = () => undefined,
	loadImage = loadBrowserImage,
	loadPageImage = loadBrowserImage,
}: RunStartupPreloadOptions): Promise<void> => {
	let lastProgress = 0;
	const reportProgress = (next: StartupPreloadProgress) => {
		lastProgress = Math.max(lastProgress, next.progress);
		onProgress({ ...next, progress: lastProgress });
	};
	reportProgress({ progress: 0.04, status: "Opening the grimoire" });
	const queryCards = () =>
		client.query({ query: DASHBOARD_CARDS_QUERY, fetchPolicy: "cache-first" });
	const pageImages = unique([...STARTUP_PAGE_IMAGE_ASSETS]);
	let settled = 0;
	const pageImagesPromise = preloadWithConcurrency(
		pageImages,
		(url) => loadWithRetry(url, loadPageImage),
		() => {
			settled += 1;
			reportProgress({
				progress: 0.08 + (settled / pageImages.length) * 0.32,
				status: "Gathering game artwork",
			});
		},
		4,
		true,
	);
	const [result] = await Promise.all([
		queryCards().catch(queryCards),
		loadGeneratedAssetManifest(),
		pageImagesPromise,
	]);
	const cards = (result.data?.dashboardCards ?? []) as PreloadCard[];
	const previews = selectStartupCardPreviews(cards);
	onCards(previews);
	reportProgress({ progress: 0.42, status: "Preparing the first hand" });
	const cardImages = unique(previews.map((preview) => preview.imageUrl));
	let cardImagesSettled = 0;
	await preloadWithConcurrency(
		cardImages,
		(url) => loadWithRetry(url, loadImage),
		() => {
			cardImagesSettled += 1;
			reportProgress({
				progress: 0.42 + (cardImagesSettled / cardImages.length) * 0.56,
				status: "Gathering game artwork",
			});
		},
		6,
		true,
	);
	reportProgress({ progress: 1, status: "The table is ready" });
};
